/**
 * NotificationToast - Rich toast notification for server notifications
 * Shows title, message, and allows click to navigate
 */
import React, { useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Animated,
  TouchableOpacity,
  Platform,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';

export interface ServerNotification {
  id: number;
  title: string;
  message: string;
  notification_type: string;
  created_at: string;
  is_read: boolean;
  link?: string;
  sender_name?: string;
  department_name?: string;
  related_object_type?: string;
  related_object_id?: number;
}

interface NotificationToastProps {
  visible: boolean;
  notification: ServerNotification | null;
  duration?: number;
  onHide: () => void;
  onPress?: () => void;
}

// Icons
const BellIcon = ({ size = 24, color = '#FFFFFF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const CheckCircleIcon = ({ size = 24, color = '#22C55E' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M9 12L11 14L15 10"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const XCircleIcon = ({ size = 24, color = '#EF4444' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M15 9L9 15M9 9L15 15"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const AlertCircleIcon = ({ size = 24, color = '#F59E0B' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M12 8V12"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M12 16H12.01"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const TargetIcon = ({ size = 24, color = '#3B82F6' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const CloseIcon = ({ size = 18, color = '#9CA3AF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M18 6L6 18M6 6L18 18"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const getNotificationConfig = (type: string, isDark: boolean) => {
  switch (type) {
    case 'goal_approved':
    case 'initiative_approved':
    case 'change_approved':
      return {
        bgColor: isDark ? '#064E3B' : '#ECFDF5',
        borderColor: '#22C55E',
        iconBgColor: isDark ? '#065F46' : '#D1FAE5',
        Icon: CheckCircleIcon,
        iconColor: '#22C55E',
      };
    case 'goal_rejected':
    case 'initiative_rejected':
    case 'change_rejected':
    case 'goal_deleted':
      return {
        bgColor: isDark ? '#7F1D1D' : '#FEF2F2',
        borderColor: '#EF4444',
        iconBgColor: isDark ? '#991B1B' : '#FEE2E2',
        Icon: XCircleIcon,
        iconColor: '#EF4444',
      };
    case 'goal_submitted':
    case 'initiative_submitted':
    case 'change_request':
      return {
        bgColor: isDark ? '#78350F' : '#FFFBEB',
        borderColor: '#F59E0B',
        iconBgColor: isDark ? '#92400E' : '#FEF3C7',
        Icon: AlertCircleIcon,
        iconColor: '#F59E0B',
      };
    case 'goal_assigned':
    case 'initiative_assigned':
    case 'goal_edited':
      return {
        bgColor: isDark ? '#1E3A5F' : '#EFF6FF',
        borderColor: '#3B82F6',
        iconBgColor: isDark ? '#1E40AF' : '#DBEAFE',
        Icon: TargetIcon,
        iconColor: '#3B82F6',
      };
    default:
      return {
        bgColor: isDark ? '#374151' : '#FFFFFF',
        borderColor: '#A9211F',
        iconBgColor: isDark ? '#4B1C1C' : '#FEF2F2',
        Icon: BellIcon,
        iconColor: '#A9211F',
      };
  }
};

export default function NotificationToast({
  visible,
  notification,
  duration = 5000,
  onHide,
  onPress,
}: NotificationToastProps) {
  const { colors, isDark } = useTheme();
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(-100)).current;

  // Theme colors for text
  const themeColors = {
    titleColor: isDark ? '#F9FAFB' : '#111827',
    messageColor: isDark ? '#D1D5DB' : '#6B7280',
    closeIconColor: isDark ? '#9CA3AF' : '#9CA3AF',
  };

  // useNativeDriver is not supported on web
  const useNativeDriver = Platform.OS !== 'web';

  useEffect(() => {
    if (visible && notification) {
      // Show animation
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver,
        }),
        Animated.spring(translateY, {
          toValue: 0,
          friction: 8,
          tension: 40,
          useNativeDriver,
        }),
      ]).start();

      // Auto hide after duration
      const timer = setTimeout(() => {
        hideToast();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [visible, notification]);

  const hideToast = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver,
      }),
      Animated.timing(translateY, {
        toValue: -100,
        duration: 200,
        useNativeDriver,
      }),
    ]).start(() => {
      onHide();
    });
  };

  if (!visible || !notification) return null;

  const config = getNotificationConfig(notification.notification_type, isDark);
  const IconComponent = config.Icon;

  return (
    <Animated.View
      style={[
        styles.container,
        {
          backgroundColor: config.bgColor,
          borderLeftColor: config.borderColor,
          opacity: fadeAnim,
          transform: [{ translateY }],
        },
      ]}
    >
      <TouchableOpacity
        style={styles.content}
        onPress={() => {
          hideToast();
          if (onPress) onPress();
        }}
        activeOpacity={0.9}
      >
        {/* Icon */}
        <View style={[styles.iconContainer, { backgroundColor: config.iconBgColor }]}>
          <IconComponent size={24} color={config.iconColor} />
        </View>

        {/* Text Content */}
        <View style={styles.textContainer}>
          <Text style={[styles.title, { color: themeColors.titleColor }]} numberOfLines={1}>
            {notification.title}
          </Text>
          <Text style={[styles.message, { color: themeColors.messageColor }]} numberOfLines={2}>
            {notification.message}
          </Text>
          {notification.department_name && (
            <View style={styles.orgBadge}>
              <Text style={styles.orgText}>{notification.department_name}</Text>
            </View>
          )}
        </View>

        {/* Close Button */}
        <TouchableOpacity
          style={styles.closeButton}
          onPress={(e) => {
            e.stopPropagation();
            hideToast();
          }}
        >
          <CloseIcon size={18} color={themeColors.closeIconColor} />
        </TouchableOpacity>
      </TouchableOpacity>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: Platform.OS === 'web' ? 20 : 50,
    left: 20,
    right: 20,
    maxWidth: 450,
    alignSelf: 'center',
    borderRadius: 12,
    borderLeftWidth: 4,
    zIndex: 9999,
    elevation: 10,
    // Platform-specific shadow
    ...(Platform.OS === 'web'
      ? {
          // @ts-ignore - boxShadow is valid for web
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.15,
          shadowRadius: 12,
        }),
  },
  content: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    padding: 16,
  },
  iconContainer: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  textContainer: {
    flex: 1,
  },
  title: {
    fontSize: 15,
    fontFamily: FONTS.semiBold,
    color: '#111827',
    textAlign: 'right',
    marginBottom: 4,
  },
  message: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
    lineHeight: 20,
  },
  orgBadge: {
    alignSelf: 'flex-end',
    backgroundColor: '#FEF2F2',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    marginTop: 8,
  },
  orgText: {
    fontSize: 11,
    fontFamily: FONTS.medium,
    color: '#A9211F',
  },
  closeButton: {
    padding: 4,
    marginRight: 8,
  },
});
